import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { State, City } from "country-state-city";
import { autocompleteDropdownStyles, getDisabledInputStyles, DISABLED_INPUT_BG } from "Constants/Style";
import { useFormikContext, useField } from "formik";
import { fetchStaffDropdown } from "slices/siteVisit/thunk";
import StatusToggle from "components/StatusToggle";

interface SiteVisitData {
  id?: string;
  site_visit_date: string;
  staff_name: string;
  staff_id?: string; // Staff ID for backend (foreign key to User)
  // Denormalized staff fields
  staff_country_code?: string;
  staff_contact_number?: string;
  staff_email?: string;
  location_url: string;
  address: string;
  city: string;
  state: string;
  remarks: string;
}

interface SiteVisitSectionProps {
  staffOptions?: any[];
  stateOptions?: any[];
  disabled?: boolean;
}

const SiteVisitSection: React.FC<SiteVisitSectionProps> = ({
  staffOptions = [],
  stateOptions = [],
  disabled = false,
}) => {
  const formik = useFormikContext<any>();
  const [siteVisitsField, siteVisitsMeta, siteVisitsHelpers] =
    useField<SiteVisitData[]>("site_visits");
  const [siteVisitEnabledField, siteVisitEnabledMeta] =
    useField("site_visit_enabled");
  const [staffNameField, staffNameMeta] = useField("site_visits[0].staff_name");
  const [staffDropdown, setStaffDropdown] = useState<any[]>([]);
  // Get the first (and only) site visit from the array, or create empty object
  const siteVisit = siteVisitsField.value?.[0] || {
    id: undefined,
    site_visit_date: "",
    staff_name: "",
    staff_id: undefined,
    staff_country_code: "",
    staff_contact_number: "",
    staff_email: "",
    location_url: "",
    address: "",
    city: "",
    state: "",
    remarks: "",
  };
  const enabled = siteVisitEnabledField.value || false;
  const dispatch = useDispatch<any>();
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  // Redux selector for staff dropdown data
  // Note: The reducer is registered as 'siteVisitReducer' in the store
  const selectStaffDropdown = createSelector(
    (state: any) => state.siteVisitReducer || state.enquiry,
    (siteVisitState) => ({
      staff: siteVisitState?.dropdowns?.staff || [],
      loading: siteVisitState?.loading || false,
      error: siteVisitState?.error || null,
    })
  );

  // Get staff dropdown data from Redux store
  const {
    staff,
    loading: staffLoading,
    error: staffError,
  } = useSelector(selectStaffDropdown);

  // Load Indian states on mount (India ISO code = "IN")
  useEffect(() => {
    const indianStates = State.getStatesOfCountry("IN");
    setStates(indianStates);

    // Fetch staff dropdown if staffOptions are not provided via props
    if (staffOptions.length === 0) {
      dispatch(fetchStaffDropdown());
    }
  }, [dispatch, staffOptions.length]);

  // Update staffDropdown when staff data changes from Redux or when staffOptions prop changes
  useEffect(() => {
    // Priority: Use staffOptions from props if provided, otherwise use Redux staff data
    if (staffOptions.length > 0) {
      setStaffDropdown(staffOptions);
    } else if (staff) {
      // Handle different response formats
      if (Array.isArray(staff)) {
        setStaffDropdown(staff);
      } else if (staff?.data && Array.isArray(staff.data)) {
        setStaffDropdown(staff.data);
      } else if (staff?.staff && Array.isArray(staff.staff)) {
        setStaffDropdown(staff.staff);
      } else {
        // If it's an object, try to extract array from common properties
        const staffArray = Object.values(staff).find((val: any) =>
          Array.isArray(val)
        ) as any[];
        setStaffDropdown(staffArray || []);
      }
    } else {
      setStaffDropdown([]);
    }
  }, [staff, staffOptions]);

  // Load cities when state is set (for edit mode)
  useEffect(() => {
    if (siteVisit.state) {
      const selectedState = states.find((s: any) => s.name === siteVisit.state);
      if (selectedState?.isoCode) {
        const stateCities = City.getCitiesOfState("IN", selectedState.isoCode);
        setCities(stateCities || []);
      }
    }
  }, [siteVisit.state, states]);

  const handleFieldChange = async (
    field: keyof SiteVisitData,
    value: string | undefined
  ) => {
    const updatedSiteVisit = {
      ...siteVisit,
      [field]: value || "",
    };
    await formik.setFieldValue("site_visits", [updatedSiteVisit]);
    await formik.setFieldTouched("site_visits", true);
    // Validate the field after setting the value
    formik.validateField("site_visits");
  };

  const handleToggle = async (enabled: boolean) => {
    await formik.setFieldValue("site_visit_enabled", enabled);
    await formik.setFieldTouched("site_visit_enabled", true);
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInFromTop {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          .fade-in-site-visit {
            animation: fadeInFromTop 0.3s ease-out;
          }
        `}
      </style>
      <Card className="shadow-sm m-0 p-0">
        <div className="d-flex align-items-center justify-content-start mb-3">
          <h6 className="card-title mb-0">Site Visit</h6>
          <StatusToggle
            isActive={enabled}
            onChange={handleToggle}
            disabled={disabled}
            style={{ marginLeft: "10px" }}
          />
        </div>

        {enabled && (
          <div className="fade-in-site-visit">
            <div className="">
              {/* <h6 className="mb-3">Assigned Staff</h6> */}
            </div>

            <Row className="mb-3 gy-3">
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">
                  Site Visit Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={siteVisit.site_visit_date || ""}
                  onChange={(e) =>
                    handleFieldChange("site_visit_date", e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched("site_visits", true)}
                  disabled={disabled}
                  placeholder={disabled ? "" : "Select date"}
                  isInvalid={
                    !!(
                      siteVisitsMeta.touched &&
                      siteVisitsMeta.error &&
                      !siteVisit.site_visit_date
                    )
                  }
                  style={{
                    backgroundColor: disabled ? DISABLED_INPUT_BG : undefined,
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {siteVisitsMeta.touched &&
                  siteVisitsMeta.error &&
                  !siteVisit.site_visit_date
                    ? siteVisitsMeta.error
                    : ""}
                </Form.Control.Feedback>
              </Col>

              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">
                  Staff Name <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={staffDropdown}
                  disabled={disabled}
                  getOptionLabel={(option: any) => {
                    if (typeof option === "string") return option;
                    // Format: "First Last" or fallback to username
                    const firstName = option.first_name?.trim() || "";
                    const lastName = option.last_name?.trim() || "";
                    if (firstName && lastName) {
                      return `${firstName} ${lastName}`;
                    } else if (firstName) {
                      return firstName;
                    } else if (lastName) {
                      return lastName;
                    }
                    return option.username || option.email || "";
                  }}
                  value={
                    staffDropdown.find((staff: any) => {
                      // Match by user_id if available, otherwise by name
                      if (
                        siteVisit.staff_id &&
                        staff.user_id === siteVisit.staff_id
                      ) {
                        return true;
                      }
                      // Match by full name
                      const staffFullName = `${
                        staff.first_name?.trim() || ""
                      } ${staff.last_name?.trim() || ""}`.trim();
                      return (
                        staffFullName === siteVisit.staff_name ||
                        staff.username === siteVisit.staff_name ||
                        staff.email === siteVisit.staff_name
                      );
                    }) || null
                  }
                  onChange={async (event, newValue: any) => {
                    if (!newValue) {
                      // Clear the site visit if no value selected
                      const updatedSiteVisit = {
                        ...siteVisit,
                        staff_name: "",
                        staff_id: undefined,
                        staff_country_code: "",
                        staff_contact_number: "",
                        staff_email: "",
                      };
                      await formik.setFieldValue("site_visits", [
                        updatedSiteVisit,
                      ]);
                      return;
                    }

                    // Extract staff information from the new data structure
                    const firstName = newValue.first_name?.trim() || "";
                    const lastName = newValue.last_name?.trim() || "";
                    const staffName =
                      firstName && lastName
                        ? `${firstName} ${lastName}`
                        : firstName || lastName || newValue.username || "";

                    // Explicitly get user_id - check multiple possible fields
                    // Priority: user_id > id > find from dropdown array
                    let staffId = null;
                    if (newValue.user_id) {
                      staffId = newValue.user_id;
                    } else if (newValue.id) {
                      staffId = newValue.id;
                    } else {
                      // If user_id is not directly available, try to find it from the dropdown array
                      // Match by name, email, or username
                      const matchedStaff = staffDropdown.find((staff: any) => {
                        const staffFullName = `${
                          staff.first_name?.trim() || ""
                        } ${staff.last_name?.trim() || ""}`.trim();
                        return (
                          staffFullName === staffName ||
                          staff.email === newValue.email ||
                          staff.username === newValue.username ||
                          (staff.first_name === firstName &&
                            staff.last_name === lastName)
                        );
                      });

                      if (matchedStaff?.user_id) {
                        staffId = matchedStaff.user_id;
                      }
                    }

                    const staffContactNumber = newValue.mobile_number || "";
                    const staffEmail = newValue.email || "";

                    // Debug log to check what we're getting
                    // console.log("Selected staff newValue:", newValue);
                    // console.log("Extracted staffId:", staffId);

                    if (!staffId) {
                      console.warn(
                        "Warning: staff_id is null/undefined. Staff object:",
                        newValue
                      );
                      console.warn("Available staffDropdown:", staffDropdown);
                    }

                    const updatedSiteVisit = {
                      ...siteVisit,
                      staff_name: staffName,
                      staff_id: staffId, // This should be the user_id from the selected staff
                      staff_country_code: "", // Not available in new structure
                      staff_contact_number: staffContactNumber,
                      staff_email: staffEmail,
                    };

                    // console.log("Updated site visit:", updatedSiteVisit);

                    await formik.setFieldValue("site_visits", [
                      updatedSiteVisit,
                    ]);
                    await formik.setFieldTouched("site_visits", true);
                    // Validate the field after setting the value
                    formik.validateField("site_visits");
                  }}
                  onBlur={() =>
                    formik.setFieldTouched("site_visits[0].staff_name", true)
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => {
                        if (typeof opt === "string") return opt;
                        const firstName = opt.first_name?.trim() || "";
                        const lastName = opt.last_name?.trim() || "";
                        if (firstName && lastName) {
                          return `${firstName} ${lastName}`;
                        } else if (firstName) {
                          return firstName;
                        } else if (lastName) {
                          return lastName;
                        }
                        return opt.username || opt.email || "";
                      }
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={disabled ? "" : "Select Staff Name"}
                      variant="outlined"
                      size="small"
                      error={staffNameMeta.touched && !siteVisit.staff_name}
                      helperText={
                        staffNameMeta.touched && !siteVisit.staff_name
                          ? "Staff name is required"
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(disabled),
                      }}
                    />
                  )}
                />
              </Col>

              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">Location URL</Form.Label>
                <Form.Control
                  type="url"
                  value={siteVisit.location_url || ""}
                  onChange={(e) =>
                    handleFieldChange("location_url", e.target.value)
                  }
                  disabled={disabled}
                  placeholder={disabled ? "" : "Enter location URL"}
                  style={{
                    backgroundColor: disabled ? DISABLED_INPUT_BG : undefined,
                  }}
                />
              </Col>

              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">Address</Form.Label>
                <Form.Control
                  type="text"
                  value={siteVisit.address || ""}
                  onChange={(e) => handleFieldChange("address", e.target.value)}
                  disabled={disabled}
                  placeholder={disabled ? "" : "Enter address"}
                  style={{
                    backgroundColor: disabled ? DISABLED_INPUT_BG : undefined,
                  }}
                />
              </Col>

              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">State</Form.Label>
                <Autocomplete
                  options={states}
                  disabled={disabled}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.name || ""
                  }
                  value={
                    states.find(
                      (state: any) => state.name === siteVisit.state
                    ) || null
                  }
                  onChange={async (event, newValue: any) => {
                    const stateName = newValue?.name || "";

                    // Update state and clear city
                    const updatedSiteVisit = {
                      ...siteVisit,
                      state: stateName,
                      city: "", // Clear city when state changes
                    };
                    await formik.setFieldValue("site_visits", [
                      updatedSiteVisit,
                    ]);
                    await formik.setFieldTouched("site_visits", true);
                    // Validate the field after setting the value
                    formik.validateField("site_visits");

                    // Load cities for selected state
                    if (newValue?.isoCode) {
                      const stateCities = City.getCitiesOfState(
                        "IN",
                        newValue.isoCode
                      );
                      setCities(stateCities || []);
                    } else {
                      setCities([]);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("site_visits", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        typeof opt === "string" ? opt : opt.name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={disabled ? "" : "Select State"}
                      variant="outlined"
                      size="small"
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(disabled),
                      }}
                    />
                  )}
                />
              </Col>

              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">City</Form.Label>
                <Autocomplete
                  options={cities}
                  disabled={disabled || !siteVisit.state}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.name || ""
                  }
                  value={
                    cities.find((city: any) => city.name === siteVisit.city) ||
                    null
                  }
                  onChange={async (event, newValue: any) => {
                    await handleFieldChange("city", newValue?.name || "");
                  }}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        typeof opt === "string" ? opt : opt.name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        disabled
                          ? ""
                          : siteVisit.state
                          ? "Select City"
                          : "Select State First"
                      }
                      variant="outlined"
                      size="small"
                      disabled={disabled || !siteVisit.state}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(disabled),
                      }}
                    />
                  )}
                />
              </Col>

              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="fw-semibold">Remarks</Form.Label>
                <Form.Control
                  type="text"
                  value={siteVisit.remarks || ""}
                  onChange={(e) => handleFieldChange("remarks", e.target.value)}
                  disabled={disabled}
                  placeholder={disabled ? "" : "Enter remarks"}
                  style={{
                    backgroundColor: disabled ? DISABLED_INPUT_BG : undefined,
                  }}
                />
              </Col>
            </Row>
          </div>
        )}
      </Card>
    </>
  );
};

export default SiteVisitSection;
